// ============================================================
// MAIN APP — Router, Layout, Navigation
// ============================================================
import './styles.css';
import { icons } from './icons.js';
import { APP_NAME } from './config.js';

// ── State ────────────────────────────────────────────────
let currentPage = null;
let currentModule = null;

function getCustomer() {
  try { return JSON.parse(localStorage.getItem('customer_user')); } catch { return null; }
}

function isLoggedIn() { return !!localStorage.getItem('customer_token'); }

// ── Route definitions ────────────────────────────────────
const routes = [
  { path: '/landing', module: () => import('./pages/landing.js'), auth: false },
  { path: '/login', module: () => import('./pages/login.js'), auth: false },
  { path: '/signup', module: () => import('./pages/signup.js'), auth: false },
  { path: '/dashboard', module: () => import('./pages/dashboard.js'), auth: true },
  { path: '/settings', module: () => import('./pages/settings.js'), auth: true },
  { path: '/billing', module: () => import('./pages/billing.js'), auth: true },
  { path: '/conversations', module: () => import('./pages/conversations.js'), auth: true },
  { path: '/support', module: () => import('./pages/support.js'), auth: true },
  { path: '/notifications', module: () => import('./pages/notifications.js'), auth: true },
  { path: '/menu', module: () => import('./pages/menu.js'), auth: true },
  { path: '/orders', module: () => import('./pages/orders.js'), auth: true }
];

const navItems = [
  { label: 'Overview', section: true },
  { path: '/dashboard', label: 'Dashboard', icon: 'dashboard' },
  { label: 'AI Chatbot Config', section: true },
  { path: '/settings', label: 'Bot Settings & FAQs', icon: 'settings' },
  { path: '/conversations', label: 'Bot Conversations', icon: 'messageCircle' },
  { label: 'Account & Billing', section: true },
  { path: '/billing', label: 'Billing History', icon: 'creditCard' },
  { label: 'Communication', section: true },
  { path: '/support', label: 'Support Chat', icon: 'users' },
  { path: '/notifications', label: 'Notifications', icon: 'bell' }
];

// ── Hash router ──────────────────────────────────────────
function getHash() {
  let hash = window.location.hash.slice(1);
  
  if (hash.includes('access_token=')) {
    sessionStorage.setItem('oauth_in_progress', 'true');
    return '/login';
  }

  if (!hash) {
    if (sessionStorage.getItem('oauth_in_progress') === 'true') {
      return '/login';
    }
    return isLoggedIn() ? '/dashboard' : '/landing';
  }

  hash = hash.split('?')[0].split('#')[0];
  
  if (hash !== '/login') {
    sessionStorage.removeItem('oauth_in_progress');
  }

  return hash;
}

function navigate(path) {
  window.location.hash = '#' + path;
}

function matchRoute(hash) {
  for (const route of routes) {
    const paramNames = [];
    const pattern = route.path.replace(/:(\w+)/g, (_, name) => {
      paramNames.push(name);
      return '([^/]+)';
    });
    const match = hash.match(new RegExp('^' + pattern + '$'));
    if (match) {
      const params = {};
      paramNames.forEach((name, i) => params[name] = match[i + 1]);
      return { route, params };
    }
  }
  return null;
}

// ── Layout rendering ─────────────────────────────────────
function renderSidebar(business) {
  const currentPath = getHash();
  const isFoodBiz = business?.business_type === 'cafe' || business?.business_type === 'restaurant';
  
  // Construct dynamic sidebar items
  const items = [...navItems];
  if (isFoodBiz) {
    const idx = items.findIndex(i => i.path === '/conversations');
    if (idx !== -1) {
      items.splice(idx + 1, 0,
        { label: 'Menu & Orders', section: true },
        { path: '/menu', label: 'Manage Menu', icon: 'coffee' },
        { path: '/orders', label: 'Track Orders', icon: 'clipboard' }
      );
    }
  }

  const navHtml = items
    .map(item => {
      if (item.section) return `<div class="sidebar__section">${item.label}</div>`;
      
      // If business is pending or rejected, restrict tabs except dashboard and support
      const isRestricted = business?.status === 'pending' || business?.status === 'rejected' || business?.status === 'REJECTED';
      const isDisabled = isRestricted && item.path !== '/dashboard' && item.path !== '/support';
      
      if (isDisabled) {
        return `<div class="nav-link nav-link--disabled" title="Unlock after application approval">${icons[item.icon] || ''}${item.label}</div>`;
      }
      
      const isActive = currentPath === item.path || (item.path !== '/dashboard' && currentPath.startsWith(item.path));
      return `<a href="#${item.path}" class="nav-link ${isActive ? 'active' : ''}">${icons[item.icon] || ''}${item.label}</a>`;
    }).join('');

  const initials = business?.business_name ? business.business_name.slice(0, 2).toUpperCase() : 'B';
  const logoHtml = business?.logo_url ? 
    `<img src="${business.logo_url}" class="sidebar__avatar-img" />` : 
    `<div class="sidebar__avatar">${initials}</div>`;

  return `
    <aside class="sidebar" id="sidebar">
      <div class="sidebar__brand" style="gap: 8px;">
        <div class="sidebar__logo" style="background: transparent; border: none; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; padding: 0;">
          ${icons.logo}
        </div>
        <div class="sidebar__name" style="font-family: var(--font-header); font-size: var(--fs-md); font-weight: 700; letter-spacing: -0.5px;">
          <span style="color: #ffffff; font-weight: 800;">Hukum</span>
        </div>
      </div>
      <nav class="sidebar__nav">${navHtml}</nav>
      <div class="sidebar__footer">
        <div class="sidebar__user">
          ${logoHtml}
          <div class="sidebar__user-info">
            <div class="sidebar__user-name">${business?.business_name || 'Business'}</div>
            <div class="sidebar__user-role">${business?.status || 'pending'}</div>
          </div>
          <button onclick="window.__logout()" title="Logout" style="color:var(--text-tertiary); background:none; border:none; cursor:pointer">${icons.logout}</button>
        </div>
      </div>
    </aside>`;
}

function renderHeader(title, business) {
  const isPending = business?.status === 'pending';
  const isRejected = business?.status === 'rejected' || business?.status === 'REJECTED';
  let badgeHtml = '';
  if (isPending) {
    badgeHtml = `<span class="badge badge--pending" style="margin-left: 12px; font-size: var(--fs-xs);">Review Pending</span>`;
  } else if (isRejected) {
    badgeHtml = `<span class="badge badge--cancelled" style="margin-left: 12px; font-size: var(--fs-xs); background-color: var(--alert-coral); color: white;">Rejected</span>`;
  } else {
    badgeHtml = `<span class="badge badge--active" style="margin-left: 12px; font-size: var(--fs-xs);">Active</span>`;
  }

  return `
    <header class="header">
      <div class="header__left">
        <button class="btn btn--icon" id="menu-toggle" style="display:none">${icons.menu}</button>
        <h1 class="header__title">${title}</h1>
        ${badgeHtml}
      </div>
    </header>`;
}

// ── Page titles ──────────────────────────────────────────
const pageTitles = {
  '/dashboard': 'Business Dashboard',
  '/settings': 'Bot Settings & FAQ Setup',
  '/conversations': 'AI Bot Conversations',
  '/billing': 'Billing & Membership',
  '/support': 'Support & Feedback Chat',
  '/notifications': 'Platform Notifications',
  '/menu': 'Menu & Dishes Management',
  '/orders': 'Restaurant Order Tracking'
};

// ── Main router ──────────────────────────────────────────
async function handleRoute() {
  const hash = getHash();
  const app = document.getElementById('app');

  // If we're already on the requested public page, do nothing (prevents reload loop during OAuth hash clear)
  if (currentPage === hash && ['/landing', '/login', '/signup'].includes(hash)) {
    return;
  }

  // Cleanup previous page
  if (currentModule?.destroy) currentModule.destroy();

  // Auth check
  const publicRoutes = ['/landing', '/login', '/signup'];
  const isPublic = publicRoutes.includes(hash);

  if (!isLoggedIn() && !isPublic) {
    navigate('/landing');
    return;
  }
  if (isLoggedIn() && isPublic) {
    navigate('/dashboard');
    return;
  }

  // Find route
  const matched = matchRoute(hash);
  if (!matched) {
    navigate(isLoggedIn() ? '/dashboard' : '/landing');
    return;
  }

  const { route, params } = matched;

  // Render public page (no layout)
  if (isPublic) {
    const mod = await route.module();
    app.innerHTML = mod.render();
    mod.init?.({ navigate });
    currentModule = mod;
    currentPage = hash;
    return;
  }

  // Render App layout for logged-in user
  let business = getCustomer();

  // Asynchronously refresh business profile in the background to handle admin status updates
  if (isLoggedIn()) {
    import('./api.js').then(({ auth }) => {
      auth.me().then(freshBiz => {
        const cachedBiz = getCustomer();
        if (JSON.stringify(freshBiz) !== JSON.stringify(cachedBiz)) {
          localStorage.setItem('customer_user', JSON.stringify(freshBiz));
          // If critical attributes changed, trigger reload of current route
          if (freshBiz.status !== cachedBiz?.status || 
              freshBiz.custom_config?.bucket_name !== cachedBiz?.custom_config?.bucket_name ||
              freshBiz.business_name !== cachedBiz?.business_name) {
            handleRoute();
          }
        }
      }).catch(err => console.error('Failed to sync customer profile:', err));
    });
  }
  
  // If business is restricted and user tries to access restricted tabs, force redirect to dashboard
  const isRestricted = business?.status === 'pending' || business?.status === 'rejected' || business?.status === 'REJECTED';
  if (isRestricted && hash !== '/dashboard' && hash !== '/support' && hash !== '/notifications') {
    navigate('/dashboard');
    return;
  }

  const title = pageTitles[hash] || 'Business Portal';
  
  app.innerHTML = `
    <div class="app-layout">
      ${renderSidebar(business)}
      <div class="main-area">
        ${renderHeader(title, business)}
        <div class="content fade-in" id="page-content">
          <div class="loading-page"><div class="spinner"></div></div>
        </div>
      </div>
    </div>`;

  // Bind menu toggle for mobile
  const menuToggle = document.getElementById('menu-toggle');
  const sidebar = document.getElementById('sidebar');
  menuToggle?.addEventListener('click', (e) => {
    e.stopPropagation();
    sidebar?.classList.toggle('open');
  });

  // Close sidebar when clicking outside or clicking any nav link
  document.addEventListener('click', (e) => {
    if (sidebar?.classList.contains('open') && !sidebar.contains(e.target) && e.target !== menuToggle) {
      sidebar.classList.remove('open');
    }
  });

  sidebar?.querySelectorAll('.nav-link').forEach(link => {
    link.addEventListener('click', () => {
      sidebar.classList.remove('open');
    });
  });

  // Load page module
  try {
    const mod = await route.module();
    const content = document.getElementById('page-content');
    content.innerHTML = mod.render(params);
    content.classList.remove('fade-in');
    void content.offsetWidth;
    content.classList.add('fade-in');
    mod.init?.(params);
    currentModule = mod;
    currentPage = hash;
  } catch (err) {
    console.error('Page load error:', err);
    document.getElementById('page-content').innerHTML = `
      <div class="empty">
        <div class="empty__title">Failed to load page</div>
        <p>${err.message}</p>
      </div>`;
  }
}

// ── Global functions ─────────────────────────────────────
window.__logout = () => {
  localStorage.removeItem('customer_token');
  localStorage.removeItem('customer_user');
  navigate('/landing');
};

window.__navigate = navigate;

// ── Init ─────────────────────────────────────────────────
window.addEventListener('hashchange', handleRoute);
document.addEventListener('DOMContentLoaded', handleRoute);
// Run routing immediately
handleRoute();
